import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import {
  Paragraph,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Button,
  Separator,
  Row,
  DangerButton,
  useStatusOverlay,
  ListItem,
  List,
} from '@fs/zion-ui'
import { NoticeLoading, ContentCheck, MediaForward, EventCensus, ContentLock } from '@fs/zion-icon'
import { css } from '@emotion/core'
import { useUser } from '@fs/zion-user'
import { encodeArk } from '@fs/zion-ark'
import { useLazyQuery, useMutation } from '@apollo/client'
import { Trans } from 'react-i18next'
import { useQueryParams } from '@fs/zion-router'
import transl from './Dialogs.transl'
import { useImageOrganizeModes, useGroupData, useImageOrganizeActions } from '../../../hooks'
import { organizeModes, workflowStatuses } from '../../../helpers'
import queries from '../../../queries'
import mutations from '../../../mutations'
import fsOperationsCallHeader from '../../../../../utils/fsOperationsCallHeader'

const loadingIcon = css`
  margin-inline-end: -6px;
`

const { IN_PROGRESS, AVAILABLE, COMPLETED } = workflowStatuses

export function NoDivisionsDialog({ overlay, doSaveNewDividers }) {
  return (
    <DialogOverlay {...overlay} title={transl.noDivisionsTitle()}>
      <Paragraph size="sm">{transl.noDivisionsBody1()}</Paragraph>
      <Paragraph size="sm">{transl.noDivisionsBody2()}</Paragraph>

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="low" onClick={overlay.close}>
            {transl.cancel()}
          </Button>
          <Button emphasis="high" onClick={doSaveNewDividers}>
            {transl.continue()}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

export const DivideStatusDialog = ({
  overlay,
  isDivideComplete,
  statusError,
  updateError,
  numItems = -1,
  percentComplete = '0',
  parentGroup,
}) => {
  const { goBackToSearchResults } = useImageOrganizeModes()
  const { initialGroup } = useGroupData()
  const { query } = useQueryParams()
  const [reserveAuditCoverages] = useMutation(mutations.reserveAuditCoverages())

  const [updateActionStatus] = useMutation(mutations.updateWorkflowActionStatus())

  const errorMessage = useMemo(() => {
    if (updateError) return transl.updateErrorMessage()
    if (statusError) return transl.statusErrorMessage()
    return null
  }, [statusError, updateError])

  const headingText = useMemo(() => {
    if (isDivideComplete && !errorMessage) return transl.dialogCompleteHeading()
    if (updateError) return transl.dialogUpdateErrorHeading()
    if (statusError) return transl.dialogStatusErrorHeading()

    return transl.dialogInProgressHeading()
  }, [isDivideComplete, errorMessage, updateError, statusError])

  const isLoading = !isDivideComplete && !errorMessage

  function goToCorrectMetadata() {
    const apids = initialGroup.imageApids || []
    const newArk = apids?.length > 0 ? encodeArk(apids[0]) : null

    const { origin, pathname } = window.location
    let newPath = `${origin}${pathname}`
    if (newArk) newPath = `${newPath.substring(0, newPath.lastIndexOf('/'))}/${newArk}`

    const auditCoverages = parentGroup?.currentWorkflowActions.find((a) => a.name === 'AuditCoverages')
    updateActionStatus({
      variables: {
        params: {
          action: 'AuditCoverages',
          newStatus: 'In Progress',
          rmsId: parentGroup.id,
          createNewWorkflowAction: !auditCoverages,
        },
      },
      context: { headers: { ...fsOperationsCallHeader } },
    })

    window.location.href = `${newPath}?groupId=${query?.groupId}&view=organize&mode=${organizeModes.METADATA_ALL}`
  }

  useEffect(() => {
    if (parseInt(percentComplete, 10) === 100) {
      reserveAuditCoverages({
        variables: { parentGroupId: parentGroup.id },
        context: { headers: { ...fsOperationsCallHeader } },
      })
    }
  }, [percentComplete, parentGroup, reserveAuditCoverages])

  const CompletedMessage = useCallback(
    () => (
      <Paragraph>
        {numItems === 1 ? transl.dialogCompleteWithoutDivision() : transl.dialogCompleteContent(numItems)}
      </Paragraph>
    ),
    [numItems]
  )

  return (
    <DialogOverlay {...overlay} title={headingText} closeOnClickAway={false} nonClosable>
      <div>
        {isLoading && (
          <>
            {transl.dialogInProgressContent()}
            <Separator size="sm" />
            <Row>
              <div css={loadingIcon}>
                <NoticeLoading />
              </div>
              {transl.dialogLoadingMessage(percentComplete || '0')}
            </Row>
          </>
        )}

        {isDivideComplete && !errorMessage && <CompletedMessage />}

        {errorMessage && errorMessage}
      </div>

      <DialogOverlayFooter>
        {(isDivideComplete || statusError) && (
          <DialogOverlayActions>
            <Button onClick={() => goBackToSearchResults(parentGroup.Id)}>{transl.dialogReturnToSearch()}</Button>
            {isDivideComplete && (
              <Button emphasis="high" onClick={goToCorrectMetadata}>
                {transl.dialogCorrectMetadata()}
              </Button>
            )}
          </DialogOverlayActions>
        )}

        {updateError && !(isDivideComplete || statusError) && (
          <DialogOverlayActions>
            <Button onClick={overlay.close}>{transl.dialogClose()}</Button>
          </DialogOverlayActions>
        )}
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

export function ResetDialog({ overlay, onResetClick }) {
  const { initialGroup, groupIndex } = useGroupData()
  const { getSkippedStatus } = useImageOrganizeActions()

  const currentGroup = useMemo(
    () => initialGroup?.childGroups?.[groupIndex] ?? initialGroup,
    [initialGroup, groupIndex]
  )

  const itemStatus = useMemo(
    () => (currentGroup.id ? getSkippedStatus(currentGroup.id) : false),
    [currentGroup, getSkippedStatus]
  )

  function closeDialogAndResetStatus() {
    overlay.handleClick()
    onResetClick()
  }

  return (
    <DialogOverlay {...overlay} title={transl.resetMessageHeader()}>
      <Paragraph size="sm">{transl.resetMessageBody(itemStatus)}</Paragraph>
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button onClick={overlay.handleClick}>{transl.cancel()}</Button>
          <DangerButton onClick={closeDialogAndResetStatus}>{transl.reset()}</DangerButton>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

export function CorrectMetadataFinishedDialog({ overlay }) {
  const { cisId } = useUser()
  const showStatusOverlay = useStatusOverlay()
  const { initialGroup: parentGroup, initialChildId, itemCount } = useGroupData()
  const { goBackToSearchResults } = useImageOrganizeModes()
  const { getSkippedStatus } = useImageOrganizeActions()

  const [fetchStatusList] = useLazyQuery(queries.imageOrganizeStatusList(), {
    fetchPolicy: 'no-cache',
  })
  const [updateWorkflowActionStatusList] = useMutation(mutations.updateWorkflowActionStatusList())

  const [loadingSummary, setLoadingSummary] = useState(false)
  const [statusData, setStatusData] = useState()
  const fetchedStatusOnce = useRef(false)

  useEffect(() => {
    async function f() {
      setLoadingSummary(true)
      const rmsIdList =
        parentGroup?.childGroups?.length > 0 ? parentGroup.childGroups.map((el) => el.id) : [initialChildId]
      const result = await fetchStatusList({
        variables: { rmsIdList },
        context: { headers: { ...fsOperationsCallHeader } },
      })
      fetchedStatusOnce.current = true

      if (result?.error) showStatusOverlay({ message: transl.groupDataStatusError(), type: 'error' })

      setLoadingSummary(false)
      setStatusData(result?.data?.imageOrganizeStatusList ?? [])
    }

    if (!loadingSummary && overlay.isOpen && !fetchedStatusOnce.current) f()
  }, [fetchStatusList, initialChildId, parentGroup, showStatusOverlay, loadingSummary, statusData, overlay])

  const statusList = useMemo(() => {
    if (loadingSummary || !Array.isArray(statusData)) return []
    return statusData
  }, [loadingSummary, statusData])

  const numCorrect = useMemo(() => {
    let total = 0
    statusList.forEach(({ correctMetadata: data }) => {
      if (data?.status === COMPLETED) total++
    })
    return total
  }, [statusList])

  const numSkipped = useMemo(() => {
    let total = 0
    statusList.forEach(({ correctMetadata: data, id }) => {
      const { status, userId } = data
      const isAvailable = status === AVAILABLE || (status === IN_PROGRESS && userId === cisId)
      if (isAvailable && getSkippedStatus(id)) total++
    })
    return total
  }, [statusList, cisId, getSkippedStatus])

  const numRemaining = useMemo(() => {
    let total = 0
    statusList.forEach(({ correctMetadata: data, id }) => {
      const { status, userId } = data
      const isAvailable = status === AVAILABLE || (status === IN_PROGRESS && userId === cisId)
      if (isAvailable && !getSkippedStatus(id)) total++
    })
    return total
  }, [statusList, cisId, getSkippedStatus])

  const idsToBeReturned = useMemo(
    () =>
      statusList
        .map(({ correctMetadata, id }) => {
          const { status } = correctMetadata
          if (status === AVAILABLE || (status === IN_PROGRESS && correctMetadata.userId === cisId)) return id
          return null
        })
        .filter(Boolean),
    [cisId, statusList]
  )

  const numInProgressByOtherUsers = useMemo(() => {
    let total = 0
    statusList.forEach(({ correctMetadata }) => {
      const { status, userId } = correctMetadata

      const isAvailable = status === AVAILABLE || (status === IN_PROGRESS && userId === cisId)
      const hasProgressByOtherUser = status === IN_PROGRESS && userId !== cisId

      if (isAvailable && hasProgressByOtherUser) total++
    })
    return total
  }, [cisId, statusList])

  const doFinish = useCallback(async () => {
    const updateInput = idsToBeReturned.map((rmsId) => ({
      rmsId,
      newStatus: AVAILABLE,
      action: 'AuditCoverages',
    }))
    if (updateInput.length) {
      const result = await updateWorkflowActionStatusList({
        variables: { actionStatusList: updateInput },
        context: { headers: { ...fsOperationsCallHeader } },
      })
      if (result.error) {
        showStatusOverlay({ message: transl.groupDataFinishError(), type: 'error' })
        return
      }
    }

    goBackToSearchResults(null, parentGroup?.naturalGroupId)
  }, [goBackToSearchResults, idsToBeReturned, parentGroup, showStatusOverlay, updateWorkflowActionStatusList])

  const SummaryList = useCallback(() => {
    if (loadingSummary) return <ListItem startElement={<NoticeLoading />} />

    const valueTypes = ['correct', 'skipped', 'remaining', 'inProgress']

    return valueTypes.map((v) => {
      const values = [numCorrect, numSkipped, numRemaining, numInProgressByOtherUsers]
      const singleTexts = [
        transl.correctSingle(),
        transl.skippedSingle(),
        transl.remainingSingle(),
        transl.inProgressByOthersSingle(),
      ]
      const multiTexts = [
        transl.correctMultiple(numCorrect),
        transl.skippedMultiple(numSkipped),
        transl.remainingMultiple(numRemaining),
        transl.inProgressByOthersMultiple(numInProgressByOtherUsers),
      ]

      const icons = [ContentCheck, MediaForward, EventCensus, ContentLock]
      const idx = valueTypes.indexOf(v)

      if (idx === -1 || values?.[idx] === 0) {
        return null
      }

      const text = values[idx] > 1 ? multiTexts[idx] : singleTexts[idx]

      return (
        <ListItem
          dense
          key={v}
          startElement={<ListItem.Icon Icon={icons[idx]} />}
          primaryText={text}
          secondaryText={idx === 3 ? transl.inProgressByOthersSubtext() : null}
        />
      )
    })
  }, [numCorrect, numInProgressByOtherUsers, numRemaining, numSkipped, loadingSummary])

  return (
    <DialogOverlay {...overlay} title={transl.summary()}>
      {transl.heresASummary(itemCount)}
      <List>
        <SummaryList />
      </List>
      <Separator size="xs" />
      <Paragraph>
        <Trans
          i18nKey="CorrectMetadataFinishedDialog.workSummaryMessage"
          defaults="You can continue making changes, or click <strong>Finish</strong> to return skipped and unreviewed items. Corrected items were submitted when you marked the metadata correct."
        />
      </Paragraph>

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button onClick={overlay.close}>{transl.continueEditing()}</Button>
          <Button emphasis="high" onClick={doFinish} disabled={loadingSummary}>
            {transl.finish()}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
